// src/pages/Chat/GroupSettingsModal.jsx
import React, { useState, useEffect } from "react";
import { FiX, FiUserPlus, FiLogOut, FiTrash2, FiUsers, FiShield } from "react-icons/fi";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import { getConversationDetail, addGroupMember, removeGroupMember, deleteConversation } from "../../api/chatService";
import { getUsers } from "../../api/accountsService";

function MemberAvatar({ photo, name }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = (name || "?")[0].toUpperCase();
  if (photo && !imgErr) {
    return (
      <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
        <img src={photo} alt={name} className="w-full h-full object-cover" onError={() => setImgErr(true)} />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold shrink-0">
      {initials}
    </div>
  );
}

export default function GroupSettingsModal({ conversation, onClose, currentUserId }) {
  const [members, setMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);

  const fetchDetail = React.useCallback(async () => {
    if (!conversation?.id) return;
    try {
      const { data } = await getConversationDetail(conversation.id);
      setMembers(data.members_detail || []);
    } catch (err) {
      console.error("Failed to fetch group details:", err);
    }
  }, [conversation?.id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleAddMembers = async (selectedIds) => {
    try {
      await addGroupMember(conversation.id, selectedIds);
      fetchDetail();
      setShowAddMember(false);
    } catch (err) {
      alert("Failed to add members");
    }
  };

  const handleRemoveMember = async (employeeId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      await removeGroupMember(conversation.id, employeeId);
      setMembers(prev => prev.filter(m => m.user_info.employee_id !== employeeId));
    } catch (err) {
      alert("Failed to remove member");
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;
    try {
      await removeGroupMember(conversation.id, currentUserId);
      onClose();
      // Optionally trigger a refresh of the conversation list
      window.location.reload(); 
    } catch (err) {
      alert("Failed to leave group");
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm("CRITICAL: Are you sure you want to delete this group? This will archive it for everyone.")) return;
    try {
      await deleteConversation(conversation.id);
      onClose();
      window.location.reload();
    } catch (err) {
      alert("Failed to delete group");
    }
  };

  const myMember = members.find(m => m.user_info.employee_id === currentUserId);
  const isAdmin = myMember?.role === "ADMIN";

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed z-[101] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md px-4">
        <div className="bg-[#1e1e1e] border border-white/10 rounded-xl shadow-2xl overflow-hidden text-slate-100 flex flex-col max-h-[80vh]">
          
          <div className="p-4 border-b border-white/5 flex items-center justify-between shrink-0">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <FiUsers className="text-blue-400" /> Group Settings
            </h2>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-500"><FiX size={20} /></button>
          </div>

          <div className="p-6 overflow-y-auto shrink min-h-0" style={{ scrollbarWidth: "thin" }}>
            <div className="mb-6 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 mx-auto flex items-center justify-center text-3xl font-bold mb-3 shadow-lg">
                {conversation.name ? conversation.name[0].toUpperCase() : "G"}
              </div>
              <h3 className="text-xl font-bold text-white">{conversation.name || "Group Chat"}</h3>
              <p className="text-slate-500 text-sm mt-1">{members.length} members</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Members</h4>
                {isAdmin && conversation.conv_type !== "SELF" && (
                  <button 
                    onClick={() => setShowAddMember(true)}
                    className="text-xs font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <FiUserPlus size={14} /> Add Member
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {members.map(member => {
                  const rawPhoto = member.user_info?.avatar_url || member.user_info?.profile_photo || null;
                  const photoUrl = resolvePhotoUrl(rawPhoto);
                  return (
                  <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group">
                    <MemberAvatar photo={photoUrl} name={member.user_info?.full_name} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate flex items-center gap-2">
                        {member.user_info.full_name}
                        {member.role === "ADMIN" && <FiShield size={12} className="text-amber-500" title="Admin" />}
                      </p>
                      <p className="text-[10px] text-slate-500 truncate">{member.user_info.employee_id}</p>
                    </div>
                    {isAdmin && member.user_info.employee_id !== currentUserId && (
                      <button 
                        onClick={() => handleRemoveMember(member.user_info.employee_id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all"
                        title="Remove member"
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          {conversation.conv_type !== "SELF" && (
            <div className="p-4 bg-white/2 border-t border-white/5 shrink-0 flex flex-col gap-2">
              <button 
                onClick={handleLeaveGroup}
                className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5 transition-colors"
              >
                <FiLogOut size={16} /> Leave Group
              </button>
              {isAdmin && (
                <button 
                  onClick={handleDeleteGroup}
                  className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-400/10 transition-colors"
                >
                  <FiTrash2 size={16} /> Delete Group
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddMember && (
        <AddMemberSubModal 
          onClose={() => setShowAddMember(false)} 
          onAdd={handleAddMembers}
          existingMemberIds={members.map(m => m.user_info.employee_id)}
        />
      )}
    </>
  );
}

function AddMemberSubModal({ onClose, onAdd, existingMemberIds }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await getUsers();
      // Use the same robust logic as NewDMModal
      const raw = Array.isArray(data)
        ? data
        : data.users || data.results || data.employees || data.data || [];
      
      const normalised = raw.map(u => ({
        employee_id: u.employee_id,
        full_name: u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.employee_id
      })).filter(u => u.employee_id);

      setUsers(normalised);
    } catch (err) {
      console.error("AddMember fetch error:", err);
      setUsers([]);
    }
  };

  const filtered = (Array.isArray(users) ? users : []).filter(u => 
    u && !existingMemberIds.includes(u.employee_id) &&
    ((u.full_name || "").toLowerCase().includes(query.toLowerCase()) || (u.employee_id || "").includes(query))
  );

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center px-4">
      <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-sm shadow-2xl overflow-hidden flex flex-col max-h-[70vh]">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <span className="font-bold text-sm">Add Members</span>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-white/10"><FiX /></button>
        </div>
        <div className="p-3 shrink-0">
          <input 
            autoFocus
            type="text" 
            placeholder="Search users..."
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-blue-500"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filtered.map(u => (
            <button 
              key={u.employee_id}
              onClick={() => setSelected(prev => prev.includes(u.employee_id) ? prev.filter(id => id !== u.employee_id) : [...prev, u.employee_id])}
              className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${selected.includes(u.employee_id) ? "bg-blue-600/20 text-blue-200" : "hover:bg-white/5 text-slate-300"}`}
            >
              <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-[10px] font-bold">
                {(u.full_name || "?")[0]}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-xs font-medium truncate">{u.full_name}</p>
                <p className="text-[10px] text-slate-500 truncate">{u.employee_id}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 text-xs font-bold text-slate-400 hover:bg-white/5 rounded-lg">Cancel</button>
          <button 
            disabled={selected.length === 0}
            onClick={() => onAdd(selected)}
            className="flex-[2] py-2 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-30"
          >
            Add {selected.length} Selected
          </button>
        </div>
      </div>
    </div>
  );
}
